import React, { useState } from "react";
import clsx from "clsx";
import { Pencil, Sparkles, Coffee, GripVertical } from "lucide-react";
import { roomColor } from "../FilterBar";
import DurationEditor from "./cells/DurationEditor";
import InlineText from "./cells/InlineText";
import FloatingCrewCell from "./FloatingCrewCell";
import ContextMenu from "./ContextMenu";
import { makeRoomMoveDragStart, TASK_GROUP_MIME } from "./roomDnd";

/**
 * Unscheduled (floating) task row rendered inside a room group in ROOMS view.
 *
 * Interaction parity with `Row.jsx`:
 *   - Single-click any cell → select (use shift / cmd for range / multi).
 *   - Double-click TASK cell → open TaskDetailDrawer.
 *   - Right-click any cell → context menu (Open / Duplicate / Delete).
 *   - Pencil affordance in the task cell → open drawer.
 *   - Selected row gets the amber ring + bg-highlight (same as scheduled rows).
 *
 * Still draggable so the producer can drop it onto a date band to schedule.
 */
export default function FloatingTaskRow({
  task,
  room,
  rowH,
  leftOffsets,
  colSpan,
  isSelected,
  onSelect,        // (e) → handle selection (shift, cmd, plain)
  onSelectTask,    // (task) → open detail drawer
  onDuplicateTask, // (task) → duplicate
  onDeleteTask,    // (task) → delete
  onPatchTask,     // (taskId, patch) → mutate the task (duration, notes…)
  onEditNotes,     // (task) → open the note editor popover
  visibleCrew = [],
  assignments = [],
  onAssignCrewToTask,
  onUnassignCrew,
  selection = [],
  selectionRef,
  onGroupWithTask,
  crewRoles,
  onAddCrewRole,
}) {
  const stripe = roomColor(room?.color_token) || "#FFB300";
  const [ctxMenu, setCtxMenu] = useState(null);
  const [groupHover, setGroupHover] = useState(false);

  const baseCell = "border-r border-line border-b border-line-subtle flex items-center px-2 text-xs relative bg-bg-base";
  const selectedRing = isSelected ? " ring-1 ring-inset ring-amber" : "";
  const cellBase = baseCell + selectedRing;
  const hatched = {
    backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 6px, rgba(255,179,0,0.05) 6px, rgba(255,179,0,0.05) 12px)",
  };

  const handleClick = (e) => {
    if (onSelect) onSelect(e);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  const openDetail = () => onSelectTask && onSelectTask(task, null);

  const ctxItems = [
    { key: "open", label: "Open details", onClick: openDetail },
    { key: "duplicate", label: "Duplicate task", onClick: () => onDuplicateTask && onDuplicateTask(task), disabled: !onDuplicateTask },
    { divider: true },
    { key: "delete", label: "Delete task", tone: "danger", onClick: () => onDeleteTask && onDeleteTask(task), disabled: !onDeleteTask },
  ];

  // Shared per-cell props so every cell selects + opens context menu.
  const cellProps = (testid) => ({
    className: cellBase,
    style: { height: rowH, position: "sticky", ...hatched },
    onClick: handleClick,
    onContextMenu: handleContextMenu,
    "data-testid": testid,
  });

  return (
    <>
      {/* START — empty */}
      <div
        {...cellProps(`floating-row-${task.id}-start`)}
        style={{ ...cellProps().style, left: leftOffsets[0], zIndex: 10 }}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("text/plain", JSON.stringify({ kind: "floating-task", task_id: task.id }));
          e.dataTransfer.effectAllowed = "move";
        }}
        title="Unscheduled — click to select, double-click title to open, drag to schedule"
      >
        <span className="mono text-2xs text-ink-mute uppercase tracking-widest">—:—</span>
      </div>
      {/* FINISH */}
      <div
        {...cellProps(`floating-row-${task.id}-finish`)}
        style={{ ...cellProps().style, left: leftOffsets[1], zIndex: 10 }}
      >
        <span className="mono text-2xs text-ink-mute uppercase tracking-widest">—:—</span>
      </div>
      {/* DUR — typable duration. Patches the task's duration_min so when this
          floating task is later scheduled, the block lands with the correct length. */}
      <div
        {...cellProps(`floating-row-${task.id}-dur`)}
        style={{ ...cellProps().style, left: leftOffsets[2], zIndex: 10 }}
      >
        <DurationEditor
          value={task.duration_min || 60}
          disabled={false}
          onCommit={(mins) => onPatchTask && onPatchTask(task.id, { duration_min: mins })}
          dataTestid={`floating-dur-${task.id}`}
        />
      </div>
      {/* DATE / "UNSCHEDULED" — also the drag handle for moving this task
          to a different room. Selection-aware via the shared roomDnd helper. */}
      <div
        {...cellProps(`floating-row-${task.id}-date`)}
        className={clsx(cellBase, "pl-0")}
        style={{ ...cellProps().style, left: leftOffsets[3], zIndex: 10 }}
        draggable
        onDragStart={makeRoomMoveDragStart({
          rowKey: `ft:${task.id}`,
          selection,
          getSelection: selectionRef ? () => selectionRef.current : undefined,
        })}
        title="Drag to move this task to a different room"
      >
        <span className="self-stretch w-1 mr-2" style={{ background: stripe }} />
        <GripVertical className="w-3 h-3 text-ink-mute mr-1 shrink-0 opacity-40" />
        <span className="mono text-2xs uppercase tracking-widest text-amber">UNSCHEDULED</span>
      </div>
      {/* TASK — inline title-edit (single click); double-click anywhere in
          the cell still opens the full drawer. Also a drop target for the
          "group with this task" interaction. */}
      <div
        {...cellProps(`floating-row-${task.id}-task`)}
        className={clsx(
          cellBase,
          groupHover && "outline outline-2 outline-amber outline-offset-[-2px] bg-amber-glow/30"
        )}
        style={{ ...cellProps().style, left: leftOffsets[4], zIndex: 10 }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          openDetail();
        }}
        draggable
        onDragStart={makeRoomMoveDragStart({
          rowKey: `ft:${task.id}`,
          selection,
          getSelection: selectionRef ? () => selectionRef.current : undefined,
        })}
        onDragOver={(e) => {
          if (!onGroupWithTask) return;
          if (!Array.from(e.dataTransfer.types).includes(TASK_GROUP_MIME)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setGroupHover(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setGroupHover(false);
        }}
        onDrop={(e) => {
          setGroupHover(false);
          const raw = e.dataTransfer.getData(TASK_GROUP_MIME);
          if (!raw || !onGroupWithTask) return;
          e.preventDefault();
          e.stopPropagation();
          try {
            const payload = JSON.parse(raw);
            if (payload.kind === "task-group") {
              onGroupWithTask({ payload, targetTaskId: task.id });
            }
          } catch (err) { console.warn(err); }
        }}
      >
        <div className="flex items-center gap-2 w-full min-w-0">
          <span className="w-4 shrink-0" />
          {task.kind === "meal" && <Coffee className="w-3 h-3 text-amber shrink-0" />}
          {(task.priority === "critical" || task.priority === "high") && (
            <Sparkles className="w-3 h-3 text-red shrink-0" title={task.priority} />
          )}
          <InlineText
            value={task.title || ""}
            disabled={false}
            onCommit={(newTitle) => {
              if (newTitle && newTitle !== task.title) {
                onPatchTask && onPatchTask(task.id, { title: newTitle });
              }
            }}
            dataTestid={`floating-title-${task.id}`}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openDetail();
            }}
            className="ml-1 text-ink-mute hover:text-amber shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            title="Open task detail"
            data-testid={`floating-open-detail-${task.id}`}
          >
            <Pencil className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
      {/* STATUS */}
      <div
        {...cellProps(`floating-row-${task.id}-status`)}
        style={{ ...cellProps().style, left: leftOffsets[5], zIndex: 10 }}
      >
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute">{task.status || "planned"}</span>
      </div>
      {/* NOTES — click to open the inline note editor (same as scheduled rows). */}
      <div
        {...cellProps(`floating-row-${task.id}-notes`)}
        style={{ ...cellProps().style, left: leftOffsets[6], zIndex: 10 }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEditNotes && onEditNotes(task);
          }}
          className={clsx(
            "flex items-center gap-1 min-w-0 flex-1 text-left text-2xs hover:text-amber transition-colors",
            task?.notes ? "text-ink-dim" : "text-ink-mute mono uppercase tracking-wider"
          )}
          title={task?.notes ? `Edit note: ${task.notes}` : "Add note"}
          data-testid={`floating-notes-edit-${task.id}`}
        >
          <Pencil className="w-2.5 h-2.5 shrink-0" />
          {task?.notes ? <span className="truncate">{task.notes}</span> : <span>+ note</span>}
        </button>
      </div>
      {/* Crew columns — one cell per visible crew. If colSpan ≤ 7 (no crew),
          we skip rendering entirely. Each cell allows pre-staging a crew
          assignment on the floating task; when the task is later scheduled,
          the assignment migrates onto the new block automatically. */}
      {colSpan > 7 && visibleCrew.map((cm) => {
        const a = assignments.find(
          (x) => x.task_id === task.id && !x.schedule_block_id && x.crew_member_id === cm.id
        );
        return (
          <FloatingCrewCell
            key={`fc-${task.id}-${cm.id}`}
            crewMember={cm}
            task={task}
            assignment={a}
            cellBase={cellBase + " " + "group"}
            rowH={rowH}
            onAssignCrewToTask={onAssignCrewToTask}
            onUnassignCrew={onUnassignCrew}
            onSelect={handleClick}
            onContextMenu={handleContextMenu}
            crewRoles={crewRoles}
            onAddCrewRole={onAddCrewRole}
          />
        );
      })}

      <ContextMenu
        open={!!ctxMenu}
        x={ctxMenu?.x}
        y={ctxMenu?.y}
        onClose={() => setCtxMenu(null)}
        items={ctxItems}
      />
    </>
  );
}
